'use client';
import MuiTimeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import { Box } from '@mui/material';
import RichText from '../../RichText';
import { TimelineDataType, TimelineItemType } from './type';
import getRichTextSummary from '../../RichText/getRichTextSummary';
import TbIcon from '../../elements/TbIcon';

const ICON_SIZE = 36;

const positionMap: Record<
  TimelineDataType['position'],
  'left' | 'right' | 'alternate' | 'alternate-reverse'
> = {
  left: 'left',
  right: 'right',
  alternate: 'alternate',
  'alternate-reverse': 'alternate-reverse',
};

const resolveDotColor = (
  color: string,
): 'primary' | 'secondary' | 'inherit' | undefined => {
  if (color === 'primary') return 'primary';
  if (color === 'secondary') return 'secondary';
  return 'inherit';
};

const DotContent = ({ item }: { item: TimelineItemType }) => {
  if (item.customIcon) {
    return <TbIcon icon={item.customIcon} size={ICON_SIZE} />;
  }
  return null;
};

type Props = { data: TimelineDataType };

const TimelineView = ({ data }: Props) => {
  const { items, position, styling } = data;

  return (
    <MuiTimeline
      position={positionMap[position]}
      sx={{ p: 0, m: 0, width: '100%', ...styling.sx }}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const dotColor = resolveDotColor(item.dotColor);
        const customColor = dotColor === 'inherit' ? item.dotColor : undefined;
        const isOppositeText = !!getRichTextSummary(item.oppositeContent);

        const dot = (
          <TimelineDot
            color={dotColor !== 'inherit' ? dotColor : undefined}
            variant={item.dotVariant === 'outlined' ? 'outlined' : 'filled'}
            sx={
              customColor
                ? { bgcolor: customColor, borderColor: customColor }
                : undefined
            }
          >
            <DotContent item={item} />
          </TimelineDot>
        );

        const mainContent = (
          <Box>
            <RichText>{item.description}</RichText>
          </Box>
        );

        return (
          <TimelineItem key={item.id ?? index}>
            {isOppositeText && (
              <TimelineOppositeContent color="text.secondary">
                <RichText>{item.oppositeContent}</RichText>
              </TimelineOppositeContent>
            )}
            <TimelineSeparator>
              {dot}
              {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>{mainContent}</TimelineContent>
          </TimelineItem>
        );
      })}
    </MuiTimeline>
  );
};

export default TimelineView;
